import React, { useState, createContext, useContext } from 'react';
// Types
type Currency = 'RM' | 'SGD';
type RideType = 'Standard' | 'Premium' | 'Shared Shuttle' | 'Airport';
type RideStatus = 'searching' | 'assigned' | 'pickup' | 'checkpoint' | 'dropoff' | 'completed';
type FlightType = 'departure' | 'arrival';
type Airport = 'Changi T1' | 'Changi T2' | 'Changi T3' | 'Changi T4';
interface Location {
  id: string;
  name: string;
  address: string;
  country: 'MY' | 'SG';
  isSaved?: boolean;
}
interface RideOption {
  id: string;
  type: RideType;
  price: number;
  duration: string;
  seats: number;
}
interface User {
  id: string;
  name: string;
  phone: string;
  isLoggedIn: boolean;
}
interface FlightDetails {
  type: FlightType;
  airline: string;
  flightNumber: string;
  airport: Airport;
  date: Date;
  time: Date;
}
interface Ride {
  id?: string;
  pickup?: Location;
  dropoff?: Location;
  type?: RideType;
  price?: number;
  currency?: Currency;
  date?: Date;
  status?: RideStatus;
  driverName?: string;
  driverRating?: number;
  driverVehicle?: string;
  driverPlate?: string;
  flightDetails?: FlightDetails;
  recommendedPickupTime?: Date;
}
interface DirectoryItem {
  id: string;
  category: 'food' | 'shopping' | 'attractions' | 'essentials' | 'culture' | 'picks';
  name: string;
  description: string;
  image: string;
  distance: string;
  location: Location;
}
interface AppContextType {
  user: User;
  setUser: React.Dispatch<React.SetStateAction<User>>;
  currentRide: Ride;
  setCurrentRide: React.Dispatch<React.SetStateAction<Ride>>;
  savedLocations: Location[];
  rideOptions: RideOption[];
  selectedRideOption: string;
  setSelectedRideOption: React.Dispatch<React.SetStateAction<string>>;
  getCurrency: (from?: Location, to?: Location) => Currency;
  directoryItems: DirectoryItem[];
  isDriverMode: boolean;
  setIsDriverMode: React.Dispatch<React.SetStateAction<boolean>>;
}
// Default values
const defaultUser: User = {
  id: '',
  name: '',
  phone: '',
  isLoggedIn: false
};
const defaultRide: Ride = {};
const defaultSavedLocations: Location[] = [{
  id: '1',
  name: 'Home',
  address: '123 Jalan Bukit Indah, JB',
  country: 'MY',
  isSaved: true
}, {
  id: '2',
  name: 'Office',
  address: '456 Orchard Road, Singapore',
  country: 'SG',
  isSaved: true
}];
const defaultRideOptions: RideOption[] = [{
  id: '1',
  type: 'Standard',
  price: 85,
  duration: '45-60 min',
  seats: 4
}, {
  id: '2',
  type: 'Premium',
  price: 120,
  duration: '45-60 min',
  seats: 4
}, {
  id: '3',
  type: 'Shared Shuttle',
  price: 60,
  duration: '60-90 min',
  seats: 7
}, {
  id: '4',
  type: 'Airport',
  price: 150,
  duration: '50-70 min',
  seats: 4
}];
const defaultDirectoryItems: DirectoryItem[] = [{
  id: '1',
  category: 'food',
  name: 'Restoran Hua Mui',
  description: 'Iconic Hainanese coffee shop serving traditional breakfast',
  image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400',
  distance: '2.3 km',
  location: {
    id: 'dir1',
    name: 'Restoran Hua Mui',
    address: '135 Jalan Trus, JB',
    country: 'MY'
  }
}, {
  id: '2',
  category: 'shopping',
  name: 'Johor Bahru City Square',
  description: 'Major shopping mall with international brands',
  image: 'https://images.unsplash.com/photo-1555529902-5261145633bf?w=400',
  distance: '1.8 km',
  location: {
    id: 'dir2',
    name: 'JB City Square',
    address: '106-108 Jalan Wong Ah Fook, JB',
    country: 'MY'
  }
}, {
  id: '3',
  category: 'attractions',
  name: 'Legoland Malaysia',
  description: 'Theme park with rides and attractions',
  image: 'https://images.unsplash.com/photo-1594818379496-da1e345b0ded?w=400',
  distance: '15 km',
  location: {
    id: 'dir3',
    name: 'Legoland Malaysia',
    address: '7 Jalan Legoland, Nusajaya',
    country: 'MY'
  }
}, {
  id: '4',
  category: 'essentials',
  name: 'Guardian Pharmacy',
  description: '24-hour pharmacy and health store',
  image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400',
  distance: '0.5 km',
  location: {
    id: 'dir4',
    name: 'Guardian JB',
    address: 'KSL City Mall, JB',
    country: 'MY'
  }
}, {
  id: '5',
  category: 'culture',
  name: 'Sultan Abu Bakar Mosque',
  description: 'Historic Victorian-style mosque overlooking the Straits',
  image: 'https://images.unsplash.com/photo-1564769625905-50e93615e769?w=400',
  distance: '3.2 km',
  location: {
    id: 'dir5',
    name: 'Sultan Abu Bakar Mosque',
    address: 'Jalan Gertak Merah, JB',
    country: 'MY'
  }
}, {
  id: '6',
  category: 'picks',
  name: 'Hiap Joo Bakery',
  description: 'Famous banana cake since 1919',
  image: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=400',
  distance: '2.1 km',
  location: {
    id: 'dir6',
    name: 'Hiap Joo Bakery',
    address: '13 Jalan Tan Hiok Nee, JB',
    country: 'MY'
  }
}, {
  id: '7',
  category: 'food',
  name: 'Maxwell Food Centre',
  description: 'Hawker centre with famous chicken rice',
  image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400',
  distance: '45 km',
  location: {
    id: 'dir7',
    name: 'Maxwell Food Centre',
    address: '1 Kadayanallur St, Singapore',
    country: 'SG'
  }
}, {
  id: '8',
  category: 'shopping',
  name: 'Orchard Road',
  description: 'Premier shopping district',
  image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400',
  distance: '50 km',
  location: {
    id: 'dir8',
    name: 'Orchard Road',
    address: 'Orchard Road, Singapore',
    country: 'SG'
  }
}];
// Create context
const AppContext = createContext<AppContextType | undefined>(undefined);
// Context provider
export const AppProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User>(defaultUser);
  const [currentRide, setCurrentRide] = useState<Ride>(defaultRide);
  const [selectedRideOption, setSelectedRideOption] = useState<string>('');
  const [isDriverMode, setIsDriverMode] = useState<boolean>(false);
  // Helper function to determine currency based on route direction
  const getCurrency = (from?: Location, to?: Location): Currency => {
    if (!from || !to) return 'RM';
    // MY → SG = RM, SG → MY = SGD
    return from.country === 'MY' && to.country === 'SG' ? 'RM' : 'SGD';
  };
  const value = {
    user,
    setUser,
    currentRide,
    setCurrentRide,
    savedLocations: defaultSavedLocations,
    rideOptions: defaultRideOptions,
    selectedRideOption,
    setSelectedRideOption,
    getCurrency,
    directoryItems: defaultDirectoryItems,
    isDriverMode,
    setIsDriverMode
  };
  return <AppContext.Provider value={value} data-id="element-136">{children}</AppContext.Provider>;
};
// Hook for using the context
export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};